import type { Me, Settings } from '../api/types'
import { fuerDunkel, gedaempftAuf, schriftAuf } from './kontrast'

/**
 * Die Farben der Reiseübersicht (#54) — und die Kette, an deren Ende sie gelten.
 *
 * Drei Werte: die Zeile einer **eigenen** Reise, die Zeile einer **fremden**,
 * und die **leere** Fläche darunter. Zwei Zeilenfarben und nicht eine, weil die
 * eigene Reise sich abheben soll (#53) — den Streifen an ihrer linken Kante
 * trägt das Stylesheet zusätzlich, denn eine Markierung, die allein an einer
 * wählbaren Farbe hängt, kann man versehentlich abschalten.
 */
export const LISTENFARBEN = [
  {
    key: 'own',
    einstellung: 'list_own_color',
    variable: '--liste-eigen',
    /**
     * **Die Schriftfarbe gehört zur Fläche und nicht zur Seite** (#73).
     *
     * Hier stand nur die Fläche, und die Schrift kam über `color: inherit`
     * aus `--text`. Im dunklen Modus springt `--text` auf Weiß, während die
     * eingestellte Fläche hell bleibt — gemessen an einem Bildschirmfoto vom
     * iPad: `#d7e3fe` mit weißer Schrift, **1,29:1**. Die Sprechblasen hatten
     * ihre Schrift längst gerechnet; die Zeilen nicht. Eine Regel an zwei
     * Stellen, von denen eine fehlte.
     */
    schrift: '--liste-eigen-text',
    leise: '--liste-eigen-leise',
    vorgabe: '#e8f0fe',
  },
  {
    key: 'other',
    einstellung: 'list_other_color',
    variable: '--liste-fremd',
    schrift: '--liste-fremd-text',
    leise: '--liste-fremd-leise',
    vorgabe: '#ffffff',
  },
  {
    key: 'bg',
    einstellung: 'list_bg_color',
    variable: '--liste-grund',
    schrift: '--liste-grund-text',
    leise: '--liste-grund-leise',
    vorgabe: '#f2f2f7',
  },
] as const

/** Der Name einer Listenfarbe, wie ihn die Oberfläche führt. */
export type Listenfarbe = (typeof LISTENFARBEN)[number]['key']

/** Ein vollständiger Satz — leer heißt „nichts gesetzt". */
export type Listensatz = Record<Listenfarbe, string>

export const LEER: Listensatz = { own: '', other: '', bg: '' }

/**
 * **Die Kette: das eigene Konto schlägt die Voreinstellung** (#54).
 *
 * Zwei Stufen, und `''` heißt jeweils „eine Stufe höher". Ganz oben heißt es
 * „wie das Erscheinungsbild" — dann wird die Variable **weggenommen** und nicht
 * gesetzt; nur so behält die dunkle Darstellung ihren eigenen Ton.
 *
 * **Vererbt und nicht kopiert**, anders als die Blasenfarbe einer Reise (#49):
 * dort soll ein laufender Bericht sein Aussehen behalten, wenn jemand anderswo
 * etwas dreht. Hier ist das Gegenteil gewünscht und steht so im Issue — „mit
 * einem Knopf auf die Voreinstellung zurück" heißt, dass sie weiter gilt,
 * solange nichts Eigenes dasteht.
 *
 * Als reine Funktion, damit ein Lauf sie messen kann.
 */
export function listenKette(
  konto: Partial<Listensatz> | null | undefined,
  vorein: Partial<Listensatz> | null | undefined,
): Listensatz {
  const nimm = (welche: Listenfarbe) => konto?.[welche] || vorein?.[welche] || ''
  return { own: nimm('own'), other: nimm('other'), bg: nimm('bg') }
}

/** Was der Server liefert, auf drei Zeichenketten gebracht. */
export function ausKonto(me: Me | undefined): Listensatz {
  return {
    own: me?.list_colors?.own ?? '',
    other: me?.list_colors?.other ?? '',
    bg: me?.list_colors?.bg ?? '',
  }
}

/** Dasselbe aus den allgemeinen Einstellungen. */
export function ausEinstellungen(werte: Settings | undefined): Listensatz {
  return {
    own: werte?.list_own_color ?? '',
    other: werte?.list_other_color ?? '',
    bg: werte?.list_bg_color ?? '',
  }
}

/**
 * Die Variablen für einen Satz Farben — **drei je Rolle statt einer** (#73).
 *
 * Was leer bleibt, kommt **nicht** vor: der Aufrufer nimmt eine fehlende
 * Variable wieder weg, statt sie auf einen Wert zu setzen — dieselbe Regel wie
 * bei den Sprechblasen. Nur so gilt wieder, was das Erscheinungsbild vorgibt,
 * und zwar in beiden Fassungen.
 *
 * **`dunkel` rechnet die Farbe um, statt sie wegzunehmen.** Eingestellt wird
 * im hellen Erscheinungsbild; abends steht dieselbe helle Fläche in einer
 * schwarzen Liste. `fuerDunkel()` dreht die Helligkeit und behält den Ton —
 * das Vorbild ist die Browser-Erweiterung *Noir*, die aus dem eingestellten
 * `#d7e3fe` ein `#1e2426` macht. Wer sich Blau eingestellt hat, sieht abends
 * ein dunkles Blau und nicht ein anderes Grau.
 */
export function listenVariablen(
  satz: Listensatz,
  dunkel = false,
): Record<string, string | null> {
  const raus: Record<string, string | null> = {}
  for (const eintrag of LISTENFARBEN) {
    const gewaehlt = satz[eintrag.key]
    const farbe = gewaehlt === '' ? '' : dunkel ? fuerDunkel(gewaehlt) : gewaehlt
    raus[eintrag.variable] = farbe === '' ? null : farbe
    raus[eintrag.schrift] = farbe === '' ? null : schriftAuf(farbe)
    raus[eintrag.leise] = farbe === '' ? null : gedaempftAuf(farbe)
  }
  return raus
}
